import { useState, useMemo } from 'react'
import { Card } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { 
  Building2, 
  MapPin, 
  Home, 
  Clock, 
  Users, 
  RefreshCw,
  Download,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  Hammer,
  Map,
  Save
} from 'lucide-react'
import { useBuildingStore } from '../../../../stores/buildingStore'
import type { 
  BuildingResult as BuildingResultType, 
  BuildingBatchResult,
  BuildingInput,
  BuildingRoom
} from '@creanizer/types'

interface BuildingResultProps {
  building: BuildingResultType
  batchResult?: BuildingBatchResult | null
  onRegenerate?: (input: BuildingInput) => void
  onRenovate?: (base: BuildingResultType, input: BuildingInput) => void
  onSwitchBuilding?: (building: BuildingResultType) => void
  onSave?: () => void
  isSaved?: boolean
}

export function BuildingResult({ 
  building, 
  batchResult,
  onRegenerate, 
  onRenovate,
  onSwitchBuilding,
  onSave,
  isSaved = false
}: BuildingResultProps) {
  const [copiedSection, setCopiedSection] = useState<string | null>(null)
  const [expandedRooms, setExpandedRooms] = useState<Set<string>>(new Set())
  const [selectedFloor, setSelectedFloor] = useState(0)
  
  const { updateBuilding, linkBuildings, buildings } = useBuildingStore()

  // バッチ結果の現在のインデックス
  const currentBatchIndex = useMemo(() => {
    if (!batchResult) return -1
    return batchResult.buildings.findIndex(b => 
      b.basicInfo.name === building.basicInfo.name
    )
  }, [batchResult, building])

  // 階層ごとの部屋を整理
  const roomsByFloor = useMemo(() => {
    const floors: Record<number, BuildingRoom[]> = {}
    building.interior.rooms.forEach(room => {
      if (!floors[room.floor]) {
        floors[room.floor] = []
      }
      floors[room.floor].push(room)
    })
    return Object.entries(floors)
      .map(([floor, rooms]) => [parseInt(floor), rooms] as [number, BuildingRoom[]])
      .sort((a, b) => b[0] - a[0])
  }, [building.interior.rooms])

  const copyToClipboard = async (text: string, section: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedSection(section)
    setTimeout(() => setCopiedSection(null), 2000)
  }

  const exportBuilding = () => {
    const dataStr = JSON.stringify(building, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
    const exportFileDefaultName = `building_${building.basicInfo.name.replace(/\s+/g, '_')}_${Date.now()}.json`
    
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
  }

  const handlePreviousBuilding = () => {
    if (batchResult && currentBatchIndex > 0 && onSwitchBuilding) {
      onSwitchBuilding(batchResult.buildings[currentBatchIndex - 1])
    }
  }

  const handleNextBuilding = () => {
    if (batchResult && currentBatchIndex < batchResult.buildings.length - 1 && onSwitchBuilding) {
      onSwitchBuilding(batchResult.buildings[currentBatchIndex + 1])
    }
  }

  const toggleRoomExpanded = (roomName: string) => {
    const newExpanded = new Set(expandedRooms)
    if (newExpanded.has(roomName)) {
      newExpanded.delete(roomName)
    } else {
      newExpanded.add(roomName)
    }
    setExpandedRooms(newExpanded)
  }

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <Card className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-2xl font-bold">{building.basicInfo.name}</h3>
            {building.basicInfo.alternativeNames && building.basicInfo.alternativeNames.length > 0 && (
              <p className="text-gray-600">
                別名: {building.basicInfo.alternativeNames.join('、')}
              </p>
            )}
            {batchResult && (
              <p className="text-sm text-muted-foreground mt-1">
                バッチ生成 {currentBatchIndex + 1} / {batchResult.buildings.length}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {onSave && (
              <Button
                variant="outline"
                onClick={onSave}
                disabled={isSaved}
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaved ? '保存済み' : '保存'}
              </Button>
            )}
            {batchResult && (
              <>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handlePreviousBuilding}
                  disabled={currentBatchIndex <= 0}
                  title="前の建造物"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleNextBuilding}
                  disabled={currentBatchIndex >= batchResult.buildings.length - 1}
                  title="次の建造物"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button
              size="icon"
              variant="outline"
              onClick={exportBuilding}
              title="エクスポート"
            >
              <Download className="h-4 w-4" />
            </Button>
            {onRenovate && (
              <Button
                size="icon"
                variant="outline"
                onClick={() => onRenovate(building, {
                  mode: 'renovation',
                  renovationBase: building,
                  language: building.metadata.language as 'ja' | 'en'
                })}
                title="リノベーション"
              >
                <Hammer className="h-4 w-4" />
              </Button>
            )}
            {onRegenerate && (
              <Button
                size="icon"
                variant="outline"
                onClick={() => onRegenerate({
                  theme: building.basicInfo.type,
                  buildingType: 'custom',
                  scale: 'house',
                  purpose: building.basicInfo.purpose,
                  language: building.metadata.language as 'ja' | 'en',
                  mode: 'standard'
                })}
                title="再生成"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <Badge variant="default">{building.basicInfo.type}</Badge>
          <Badge variant="secondary">{building.basicInfo.scale}</Badge>
          <Badge variant="outline">{building.basicInfo.currentState}</Badge>
          {building.basicInfo.owner && (
            <Badge variant="outline">所有: {building.basicInfo.owner}</Badge>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-gray-700">
            {building.basicInfo.summary}
          </p>
          <p className="text-gray-600 text-sm">
            {building.basicInfo.description}
          </p>
          <p className="text-sm font-medium">
            重要性: {building.basicInfo.significance}
          </p>
        </div>
      </Card>

      {/* タブコンテンツ */}
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="basic">基本情報</TabsTrigger>
          <TabsTrigger value="exterior">外観</TabsTrigger>
          <TabsTrigger value="interior">内部</TabsTrigger>
          <TabsTrigger value="history">歴史</TabsTrigger>
          <TabsTrigger value="inhabitants">住人</TabsTrigger>
        </TabsList>

        {/* 基本情報タブ */}
        <TabsContent value="basic">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  基本情報
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">名称:</span> {building.basicInfo.name}
                  </div>
                  <div>
                    <span className="font-medium">タイプ:</span> {building.basicInfo.type}
                  </div>
                  <div>
                    <span className="font-medium">規模:</span> {building.basicInfo.scale}
                  </div>
                  <div>
                    <span className="font-medium">用途:</span> {building.basicInfo.purpose}
                  </div>
                  {building.basicInfo.secondaryPurposes && (
                    <div className="col-span-2">
                      <span className="font-medium">副次用途:</span> {building.basicInfo.secondaryPurposes.join('、')}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">現状:</span> {building.basicInfo.currentState}
                  </div>
                  {building.basicInfo.owner && (
                    <div>
                      <span className="font-medium">所有者:</span> {building.basicInfo.owner}
                    </div>
                  )}
                  {building.basicInfo.manager && (
                    <div>
                      <span className="font-medium">管理者:</span> {building.basicInfo.manager}
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              <div>
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium">概要</h5>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(building.basicInfo.summary, 'summary')}
                  >
                    {copiedSection === 'summary' ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
                <p className="text-sm text-gray-600">
                  {building.basicInfo.summary}
                </p>
              </div>

              <div>
                <h5 className="font-medium mb-1">詳細説明</h5>
                <p className="text-sm text-gray-600">
                  {building.basicInfo.description}
                </p>
              </div>

              <div>
                <h5 className="font-medium mb-1">重要性</h5>
                <p className="text-sm text-gray-600">
                  {building.basicInfo.significance}
                </p>
              </div>

            </div>
          </Card>
        </TabsContent>

        {/* 外観タブ */}
        <TabsContent value="exterior">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  外観・建築
                </h4>
                <p className="text-sm mb-2">
                  <span className="font-medium">建築様式:</span> {building.exterior.architecturalStyle}
                </p>
                <p className="text-sm text-gray-600">
                  {building.exterior.visualDescription}
                </p>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium mb-2">寸法</h5>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-muted-foreground">高さ:</span> {building.exterior.dimensions.height}</div>
                    <div><span className="text-muted-foreground">幅:</span> {building.exterior.dimensions.width}</div>
                    <div><span className="text-muted-foreground">奥行:</span> {building.exterior.dimensions.depth}</div>
                    <div><span className="text-muted-foreground">総面積:</span> {building.exterior.dimensions.totalArea}</div>
                    <div><span className="text-muted-foreground">階数:</span> {building.exterior.dimensions.floors}階</div>
                    {building.exterior.dimensions.basementFloors && (
                      <div><span className="text-muted-foreground">地下:</span> {building.exterior.dimensions.basementFloors}階</div>
                    )}
                    {building.exterior.dimensions.towers && (
                      <div><span className="text-muted-foreground">塔:</span> {building.exterior.dimensions.towers}基</div>
                    )}
                  </div>
                </div>

                <div>
                  <h5 className="font-medium mb-2">建材</h5>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-muted-foreground">主要:</span> {building.exterior.materials.primary}</div>
                    {building.exterior.materials.secondary && (
                      <div><span className="text-muted-foreground">副次:</span> {building.exterior.materials.secondary.join('、')}</div>
                    )}
                    {building.exterior.materials.decorative && (
                      <div><span className="text-muted-foreground">装飾:</span> {building.exterior.materials.decorative.join('、')}</div>
                    )}
                    {building.exterior.materials.special && (
                      <div><span className="text-muted-foreground">特殊:</span> {building.exterior.materials.special.join('、')}</div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-2">ファサード</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">正面入口:</span> {building.exterior.facade.mainEntrance}</div>
                  <div><span className="text-muted-foreground">窓:</span> {building.exterior.facade.windows}</div>
                  <div><span className="text-muted-foreground">扉数:</span> {building.exterior.facade.doors}箇所</div>
                  {building.exterior.facade.balconies && (
                    <div><span className="text-muted-foreground">バルコニー:</span> {building.exterior.facade.balconies}</div>
                  )}
                  <div><span className="text-muted-foreground">屋根様式:</span> {building.exterior.facade.roofStyle}</div>
                  <div><span className="text-muted-foreground">配色:</span> {building.exterior.facade.colorScheme.join('、')}</div>
                </div>
              </div>

              {building.exterior.surroundings && (
                <div>
                  <h5 className="font-medium mb-2">周辺環境</h5>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-muted-foreground">景観:</span> {building.exterior.surroundings.landscape}</div>
                    {building.exterior.surroundings.nearbyStructures && (
                      <div><span className="text-muted-foreground">近隣建造物:</span> {building.exterior.surroundings.nearbyStructures.join('、')}</div>
                    )}
                    <div><span className="text-muted-foreground">アクセス:</span> {building.exterior.surroundings.accessibility}</div>
                  </div>
                </div>
              )}

              {building.exterior.structuralFeatures && (
                <div>
                  <h5 className="font-medium mb-2">構造的特徴</h5>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-muted-foreground">耐荷重:</span> {building.exterior.structuralFeatures.loadBearing}</div>
                    <div><span className="text-muted-foreground">基礎:</span> {building.exterior.structuralFeatures.foundation}</div>
                    <div><span className="text-muted-foreground">風化:</span> {building.exterior.structuralFeatures.weathering}</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* 内部タブ */}
        <TabsContent value="interior">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  内部構造
                </h4>
                <p className="text-sm text-gray-600">
                  {building.interior.ambiance}
                </p>
              </div>

              <Separator />

              <div>
                <h5 className="font-medium mb-2">レイアウト</h5>
                <p className="text-sm text-gray-600 mb-2">
                  {building.interior.layout.floorPlan}
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {building.interior.layout.zoning?.public && (
                    <div><span className="text-muted-foreground">公共エリア:</span> {building.interior.layout.zoning.public.join('、')}</div>
                  )}
                  {building.interior.layout.zoning?.private && (
                    <div><span className="text-muted-foreground">プライベート:</span> {building.interior.layout.zoning.private.join('、')}</div>
                  )}
                  {building.interior.layout.zoning?.restricted && (
                    <div><span className="text-muted-foreground">制限エリア:</span> {building.interior.layout.zoning.restricted.join('、')}</div>
                  )}
                  {building.interior.layout.zoning?.service && (
                    <div><span className="text-muted-foreground">サービス:</span> {building.interior.layout.zoning.service.join('、')}</div>
                  )}
                </div>
              </div>

              <div>
                <h5 className="font-medium mb-2">階層ビュー</h5>
                <div className="flex gap-2 mb-3">
                  {roomsByFloor.map(([floor], index) => (
                    <Button
                      key={floor}
                      size="sm"
                      variant={selectedFloor === index ? 'default' : 'outline'}
                      onClick={() => setSelectedFloor(index)}
                    >
                      {floor > 0 ? `${floor}階` : floor === 0 ? '地上階' : `地下${Math.abs(floor)}階`}
                    </Button>
                  ))}
                </div>
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {roomsByFloor[selectedFloor]?.[1].map((room, index) => (
                      <div 
                        key={index} 
                        className="border rounded-lg p-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleRoomExpanded(room.name)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-sm">{room.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {room.purpose} • {room.size}
                            </div>
                          </div>
                        </div>
                        {expandedRooms.has(room.name) && (
                          <div className="mt-2 text-xs text-gray-600">
                            <p>{room.description}</p>
                            {room.features && (
                              <div className="mt-1">
                                <span className="font-medium">特徴:</span> {room.features.join('、')}
                              </div>
                            )}
                            {room.connections && (
                              <div className="mt-1">
                                <span className="font-medium">接続:</span> {room.connections.join('、')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              <div>
                <h5 className="font-medium mb-2">内装スタイル</h5>
                <div className="space-y-1 text-sm">
                  <div><span className="text-muted-foreground">全体:</span> {building.interior.decorStyle.overall}</div>
                  <div><span className="text-muted-foreground">素材:</span> {building.interior.decorStyle.materials.join('、')}</div>
                  <div><span className="text-muted-foreground">カラーパレット:</span> {building.interior.decorStyle.colorPalette.join('、')}</div>
                  <div><span className="text-muted-foreground">照明:</span> {building.interior.decorStyle.lighting}</div>
                  <div><span className="text-muted-foreground">家具:</span> {building.interior.decorStyle.furnishing}</div>
                </div>
              </div>

              {building.interior.specialFeatures && (
                <div>
                  <h5 className="font-medium mb-2">特殊要素</h5>
                  <div className="space-y-1 text-sm">
                    {building.interior.specialFeatures.secretRooms && (
                      <div><span className="text-muted-foreground">秘密の部屋:</span> {building.interior.specialFeatures.secretRooms.join('、')}</div>
                    )}
                    {building.interior.specialFeatures.hiddenPassages && (
                      <div><span className="text-muted-foreground">隠し通路:</span> {building.interior.specialFeatures.hiddenPassages.join('、')}</div>
                    )}
                    {building.interior.specialFeatures.uniqueSpaces && (
                      <div><span className="text-muted-foreground">独特な空間:</span> {building.interior.specialFeatures.uniqueSpaces.join('、')}</div>
                    )}
                    {building.interior.specialFeatures.architecturalElements && (
                      <div><span className="text-muted-foreground">建築要素:</span> {building.interior.specialFeatures.architecturalElements.join('、')}</div>
                    )}
                  </div>
                </div>
              )}

              {building.interior.infrastructure && (
                <div>
                  <h5 className="font-medium mb-2">インフラ</h5>
                  <div className="space-y-1 text-sm">
                    {building.interior.infrastructure.plumbing && (
                      <div><span className="text-muted-foreground">配管:</span> {building.interior.infrastructure.plumbing}</div>
                    )}
                    {building.interior.infrastructure.electrical && (
                      <div><span className="text-muted-foreground">電気:</span> {building.interior.infrastructure.electrical}</div>
                    )}
                    {building.interior.infrastructure.heating && (
                      <div><span className="text-muted-foreground">暖房:</span> {building.interior.infrastructure.heating}</div>
                    )}
                    {building.interior.infrastructure.ventilation && (
                      <div><span className="text-muted-foreground">換気:</span> {building.interior.infrastructure.ventilation}</div>
                    )}
                    {building.interior.infrastructure.magicalSystems && (
                      <div><span className="text-muted-foreground">魔法システム:</span> {building.interior.infrastructure.magicalSystems.join('、')}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* 歴史タブ */}
        <TabsContent value="history">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  歴史・逸話
                </h4>
              </div>

              <div>
                <h5 className="font-medium mb-2">建設情報</h5>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">建設年:</span> {building.history.construction.year}</div>
                  <div><span className="text-muted-foreground">期間:</span> {building.history.construction.duration}</div>
                  {building.history.construction.architect && (
                    <div><span className="text-muted-foreground">建築家:</span> {building.history.construction.architect}</div>
                  )}
                  {building.history.construction.builder && (
                    <div><span className="text-muted-foreground">建設者:</span> {building.history.construction.builder}</div>
                  )}
                  <div><span className="text-muted-foreground">依頼者:</span> {building.history.construction.commissioner}</div>
                  <div><span className="text-muted-foreground">目的:</span> {building.history.construction.purpose}</div>
                  {building.history.construction.cost && (
                    <div><span className="text-muted-foreground">費用:</span> {building.history.construction.cost}</div>
                  )}
                </div>
              </div>

              <Separator />

              <div>
                <h5 className="font-medium mb-2">タイムライン</h5>
                <div className="space-y-3">
                  {building.history.timeline.map((event, index) => (
                    <div key={index} className="border-l-2 border-primary pl-4 pb-2">
                      <div className="font-medium text-sm">{event.era}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {event.event}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        影響: {event.impact}
                      </div>
                      {event.changes && (
                        <div className="text-xs text-gray-500 mt-1">
                          変更: {event.changes.join('、')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {building.history.anecdotes && building.history.anecdotes.length > 0 && (
                <div>
                  <h5 className="font-medium mb-2">逸話</h5>
                  <div className="space-y-3">
                    {building.history.anecdotes.map((anecdote, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <div className="font-medium mb-1">{anecdote.title}</div>
                        {anecdote.date && (
                          <div className="text-xs text-muted-foreground mb-2">{anecdote.date}</div>
                        )}
                        <div className="text-sm text-gray-600">
                          {anecdote.description}
                        </div>
                        {anecdote.type && (
                          <div className="text-xs mt-2">
                            <Badge variant="outline">{anecdote.type}</Badge>
                          </div>
                        )}
                        {anecdote.truth && (
                          <div className="text-xs mt-2">
                            <span className="font-medium">真実:</span> {anecdote.truth}
                          </div>
                        )}
                        {anecdote.influence && (
                          <div className="text-xs mt-1">
                            <span className="font-medium">影響:</span> {anecdote.influence}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </Card>
        </TabsContent>

        {/* 住人タブ */}
        <TabsContent value="inhabitants">
          <Card className="p-6">
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  住人・利用者
                </h4>
              </div>

              {building.inhabitants.current.residents && (
                <div>
                  <h5 className="font-medium mb-2">現在の住人</h5>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm">
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div><span className="font-medium">タイプ:</span> {building.inhabitants.current.residents.type}</div>
                      <div><span className="font-medium">人数:</span> {building.inhabitants.current.residents.number}人</div>
                      {building.inhabitants.current.residents.demographics && (
                        <div className="col-span-2">
                          <span className="font-medium">人口統計:</span> {building.inhabitants.current.residents.demographics}
                        </div>
                      )}
                    </div>
                    <p className="text-gray-600">
                      {building.inhabitants.current.residents.description}
                    </p>
                    {building.inhabitants.current.residents.notable && (
                      <div className="mt-2">
                        <span className="font-medium">注目すべき住人:</span>
                        <ul className="list-disc list-inside text-gray-600 mt-1">
                          {building.inhabitants.current.residents.notable.map((person, index) => (
                            <li key={index}>{person}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {building.inhabitants.current.staff && (
                <div>
                  <h5 className="font-medium mb-2">スタッフ</h5>
                  <div className="text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="font-medium">タイプ:</span> {building.inhabitants.current.staff.type}</div>
                      <div><span className="font-medium">人数:</span> {building.inhabitants.current.staff.number}人</div>
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">役割:</span> {building.inhabitants.current.staff.roles.join('、')}
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">スケジュール:</span> {building.inhabitants.current.staff.schedule}
                    </div>
                    {building.inhabitants.current.staff.hierarchy && (
                      <div className="mt-1">
                        <span className="font-medium">階層:</span> {building.inhabitants.current.staff.hierarchy}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {building.inhabitants.current.visitors && (
                <div>
                  <h5 className="font-medium mb-2">訪問者</h5>
                  <div className="text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="font-medium">平均日次:</span> {building.inhabitants.current.visitors.averageDaily}人</div>
                      <div><span className="font-medium">ピーク時間:</span> {building.inhabitants.current.visitors.peakTimes}</div>
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">訪問者タイプ:</span> {building.inhabitants.current.visitors.types.join('、')}
                    </div>
                    <div className="mt-1">
                      <span className="font-medium">目的:</span> {building.inhabitants.current.visitors.purposes.join('、')}
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              <div>
                <h5 className="font-medium mb-2">歴史的な住人</h5>
                <div className="space-y-3">
                  {building.inhabitants.historical.famousInhabitants.map((person, index) => (
                    <div key={index} className="border-l-2 border-primary pl-4">
                      <div className="font-medium text-sm">
                        {person.name} <span className="text-xs text-muted-foreground">({person.period})</span>
                      </div>
                      <div className="text-xs text-gray-600">
                        役割: {person.role}
                      </div>
                      <div className="text-xs mt-1">
                        <span className="font-medium">功績:</span> {Array.isArray(person.achievements) ? person.achievements.join('、') : person.achievements || '不明'}
                      </div>
                      <div className="text-xs mt-1">
                        <span className="font-medium">遺産:</span> {person.legacy}
                      </div>
                      {person.rooms && (
                        <div className="text-xs mt-1">
                          <span className="font-medium">使用部屋:</span> {Array.isArray(person.rooms) ? person.rooms.join('、') : person.rooms}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>



              {building.inhabitants.dailyLife && (
                <div>
                  <h5 className="font-medium mb-2">日常生活</h5>
                  <div className="text-sm space-y-1">
                    {building.inhabitants.dailyLife.morningRoutine && (
                      <div><span className="font-medium">朝:</span> {building.inhabitants.dailyLife.morningRoutine}</div>
                    )}
                    {building.inhabitants.dailyLife.afternoonActivities && (
                      <div><span className="font-medium">午後:</span> {building.inhabitants.dailyLife.afternoonActivities}</div>
                    )}
                    {building.inhabitants.dailyLife.eveningGatherings && (
                      <div><span className="font-medium">夕方:</span> {building.inhabitants.dailyLife.eveningGatherings}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  )
}
